import React from 'react';
import { useVideoConference } from '../../../../hooks/videoConference';

interface VideoConferenceFloatingProps {
  onClose?: () => void;
}

const VideoConferenceFloating = ({
  onClose,
}: VideoConferenceFloatingProps): React.ReactElement => {
  const {
    isOpen,
    position,
    size,
    minimized,
    setIsOpen,
    setPosition,
    setSize,
    setMinimized,
    url,
  } = useVideoConference();

  const containerRef = React.useRef<HTMLDivElement>(null);

  const dragging = React.useRef<boolean>(false);

  const dragOffset = React.useRef<{
    x: number;
    y: number;
  }>({
    x: 0,
    y: 0,
  });

  /**
   * Arrastar janela
   */
  React.useEffect(() => {
    const handlePointerMove = (event: PointerEvent): void => {
      if (!dragging.current) {
        return;
      }

      const newX = event.clientX - dragOffset.current.x;
      const newY = event.clientY - dragOffset.current.y;

      const maxX = Math.max(0, window.innerWidth - size.width);
      const maxY = Math.max(0, window.innerHeight - size.height);

      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    };

    const handlePointerUp = (): void => {
      dragging.current = false;
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [size.width, size.height, setPosition]);

  /**
   * Inicia o arraste da janela
   */
  const handlePointerDown = (
    event: React.PointerEvent<HTMLDivElement>,
  ): void => {
    if (!containerRef.current) {
      return;
    }

    const rect = containerRef.current.getBoundingClientRect();

    dragging.current = true;

    dragOffset.current = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };

    event.currentTarget.setPointerCapture(event.pointerId);
  };

  /**
   * Inicia o redimensionamento
   */
  const handleResizeStart = (
    event: React.PointerEvent<HTMLDivElement>,
  ): void => {
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const startY = event.clientY;

    const startWidth = size.width;
    const startHeight = size.height;

    const handlePointerMove = (moveEvent: PointerEvent): void => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;

      const newWidth = Math.max(280, startWidth + deltaX);
      const newHeight = Math.max(400, startHeight + deltaY);

      const maxWidth = Math.max(280, window.innerWidth - position.x);
      const maxHeight = Math.max(400, window.innerHeight - position.y);

      setSize({
        width: Math.min(newWidth, maxWidth),
        height: Math.min(newHeight, maxHeight),
      });
    };

    const handlePointerUp = (): void => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  };

  /**
   * Fecha a videoconferência
   */
  const handleClose = (): void => {
    setIsOpen(false);

    if (onClose) {
      onClose();
    }
  };

  return (
    <>
      {isOpen && (
        <div
          ref={containerRef}
          style={{
            position: 'fixed',
            left: position.x,
            top: position.y,
            width: minimized ? 220 : size.width,
            height: minimized ? 48 : size.height,
            zIndex: 9999,
            background: '#111827',
            borderRadius: 12,
            overflow: 'hidden',
            boxShadow: '0 10px 30px rgba(0,0,0,0.30)',
            border: '1px solid rgba(255,255,255,0.15)',
            transition: minimized ? 'width 0.2s, height 0.2s' : undefined,
          }}
        >
          {/* Barra superior */}
          <div
            onPointerDown={handlePointerDown}
            style={{
              height: 48,
              background: '#1f2937',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 10px',
              cursor: 'grab',
              userSelect: 'none',
              touchAction: 'none',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                color: '#fff',
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: '#22c55e',
                }}
              />
              Videoconferência
            </div>

            <div
              style={{
                display: 'flex',
                gap: 4,
              }}
              onPointerDown={(
                event: React.PointerEvent<HTMLDivElement>,
              ): void => {
                event.stopPropagation();
              }}
            >
              {/* Minimizar / Expandir */}
              <button
                type="button"
                onClick={(): void => {
                  setMinimized((value: boolean) => !value);
                }}
                style={{
                  width: 30,
                  height: 30,
                  border: 0,
                  borderRadius: 6,
                  background: 'rgba(255,255,255,0.1)',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: 18,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                title={minimized ? 'Expandir' : 'Minimizar'}
              >
                {minimized ? '□' : '−'}
              </button>

              {/* Fechar */}
              <button
                type="button"
                onClick={handleClose}
                style={{
                  display: 'none',
                  width: 30,
                  height: 30,
                  border: 0,
                  borderRadius: 6,
                  background: 'rgba(255,255,255,0.1)',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: 18,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                title="Fechar"
              >
                ×
              </button>
            </div>
          </div>

          {/* Videoconferência */}
          {isOpen && !minimized && (
            <iframe
              src={url || ''}
              title="Videoconferência"
              allow="camera; microphone; fullscreen; display-capture"
              style={{
                width: '100%',
                height: 'calc(100% - 48px)',
                border: 0,
                display: 'block',
                background: '#000',
              }}
            />
          )}

          {/* Alça de redimensionamento */}
          {!minimized && (
            <div
              onPointerDown={handleResizeStart}
              style={{
                position: 'absolute',
                right: 0,
                bottom: 0,
                width: 20,
                height: 20,
                cursor: 'nwse-resize',
                touchAction: 'none',
              }}
            />
          )}
        </div>
      )}
    </>
  );
};

export default VideoConferenceFloating;
